import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, Fab, IconButton, Typography, CircularProgress } from "@mui/material";
import { useHistory } from "react-router";
import ReactMarkdown from "react-markdown";
import gfm from "remark-gfm";
import MessageOutlined from "@ant-design/icons/es/icons/MessageOutlined";
import MinusOutlined from "@ant-design/icons/es/icons/MinusOutlined";
import SendOutlined from "@ant-design/icons/es/icons/SendOutlined";
import BugOutlined from "@ant-design/icons/es/icons/BugOutlined";
import TextField from "../../design-library/TextField/TextField.component";
import { get } from "../../libs/helpers/ioc";
import { l } from "../../libs/localization";
import { chatWithAgentApi } from "../../api/agent.api";
import { useAgentChatState } from "../../context/AgentChatContext";
import { computeTurnCost, countTurnTokens } from "./tokenPricing";
import AgentChatDebugPanel from "./AgentChatDebugPanel.component";
import AgentChatToolCall from "./AgentChatToolCall.component";

const markdownBubbleStyles = {
  fontSize: "0.875rem",
  lineHeight: 1.43,
  "& > *:first-of-type": { marginTop: 0 },
  "& > *:last-child": { marginBottom: 0 },
  "& p": { margin: "0 0 8px 0" },
  "& ul, & ol": { margin: "0 0 8px 0", paddingLeft: "20px" },
  "& li": { marginBottom: "2px" },
  "& a": { color: "inherit", textDecoration: "underline" },
  "& strong": { fontWeight: 700 },
  "& blockquote": {
    margin: "0 0 8px 0",
    paddingLeft: "8px",
    borderLeft: "3px solid currentColor",
    opacity: 0.85,
  },
  "& pre": {
    margin: "0 0 8px 0",
    padding: "6px 8px",
    borderRadius: "4px",
    backgroundColor: "rgba(0,0,0,0.08)",
    overflowX: "auto",
    fontSize: "0.8125rem",
  },
  "& code": {
    fontFamily: "monospace",
    backgroundColor: "rgba(0,0,0,0.08)",
    borderRadius: "4px",
    padding: "1px 4px",
    fontSize: "0.8125rem",
  },
  "& pre code": { backgroundColor: "transparent", padding: 0 },
  "& table": {
    borderCollapse: "collapse",
    margin: "0 0 8px 0",
    display: "block",
    overflowX: "auto",
  },
  "& th, & td": {
    border: "1px solid rgba(0,0,0,0.15)",
    padding: "4px 8px",
    textAlign: "left",
  },
  "& th": { fontWeight: 700, backgroundColor: "rgba(0,0,0,0.04)" },
};

// Assistant messages carry the full Claude content-block array (text, thinking,
// tool_use/tool_result, compaction, ...) so it can be echoed back verbatim on the
// next request. Only the text blocks are rendered here.
function getDisplayText(content) {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .filter((block) => block.type === "text")
    .map((block) => block.text)
    .join("\n");
}

const DEFAULT_SIZE = { width: 340, height: 460 };
const MIN_WIDTH = 280;
const MIN_HEIGHT = 320;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 900;
const VIEWPORT_MARGIN = 48;

function AgentChat() {
  const permissions = get("scopes") || {};
  const hasAgentPermission = !!permissions.agent;
  const history = useHistory();

  // Backed by Context (not local state) so it survives this component
  // remounting on route navigation — see context/AgentChatContext.js.
  const {
    open,
    setOpen,
    messages,
    setMessages,
    usageStats,
    setUsageStats,
    debugLog,
    setDebugLog,
    debugOpen,
    setDebugOpen,
  } = useAgentChatState();
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [error, setError] = useState(null);
  const [size, setSize] = useState(DEFAULT_SIZE);
  const listRef = useRef(null);
  const resizeStateRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  useEffect(() => {
    function handleResizeMove(e) {
      const state = resizeStateRef.current;
      if (!state) return;
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;
      const maxWidth = Math.min(MAX_WIDTH, window.innerWidth - VIEWPORT_MARGIN);
      const maxHeight = Math.min(MAX_HEIGHT, window.innerHeight - VIEWPORT_MARGIN);
      setSize({
        width: Math.min(maxWidth, Math.max(MIN_WIDTH, state.startWidth - dx)),
        height: Math.min(maxHeight, Math.max(MIN_HEIGHT, state.startHeight - dy)),
      });
    }
    function handleResizeEnd() {
      resizeStateRef.current = null;
    }
    document.addEventListener("mousemove", handleResizeMove);
    document.addEventListener("mouseup", handleResizeEnd);
    return () => {
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeEnd);
    };
  }, []);

  if (!hasAgentPermission) return null;

  function handleResizeStart(e) {
    e.preventDefault();
    resizeStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startWidth: size.width,
      startHeight: size.height,
    };
  }

  function performAction(action) {
    if (!action) return;
    if (action.type === "navigate_to_company_search") {
      // Pushed as router state (not a query string) so nested filters
      // (financial_filters, nielsen_filters, ...) don't need URL serialization,
      // and location.key still changes so useCompanySearch can react even
      // if we're already on this page. This is client-side routing — no
      // full page reload, so the chat history in this component survives.
      history.push({ pathname: "/companies", state: { filters: action.filters || {} } });
    }
  }

  async function handleSend() {
    const message = input.trim();
    if (!message || loading) return;
    const chatHistory = messages.map(({ role, content }) => ({ role, content }));

    setMessages((prev) => [...prev, { role: "user", content: message }]);
    setInput("");
    setError(null);
    setStatusText(l("AGENT_CHAT.THINKING"));
    setLoading(true);
    try {
      const result = await chatWithAgentApi({ message, history: chatHistory }, (label) =>
        setStatusText(label)
      );
      // Extracted from `iterations` (only set for client-side tools like
      // navigate_to_company_search and get_search_guide — MCP tools are
      // resolved inside Anthropic's connector, so we never see their
      // input/output) and attached to the assistant message so it can be
      // shown inline as its own bubble, in call order, before the model's own
      // reply. Only {role, content} is ever sent back as history (see
      // chatHistory above), so this extra field never reaches the API.
      const toolCalls = (result?.iterations || []).flatMap(
        (it) => it.toolUses || []
      );
      // Guard against a malformed/empty response ever storing `content: undefined` —
      // that would fail Joi's required() check on every future request in this
      // conversation, permanently bricking the chat until the page is reloaded.
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: result?.content || [], toolCalls },
      ]);
      performAction(result?.action);
      // TEMP DEBUG: accumulate token/cost usage for this conversation, and log
      // a per-turn (and per-iteration, if navigate_to_company_search was
      // resolved) breakdown for the debug panel.
      if (result?.usage) {
        setUsageStats((prev) => ({
          tokens: prev.tokens + countTurnTokens(result.usage),
          cost: prev.cost + computeTurnCost(result.usage, result.model),
        }));
        setDebugLog((prev) => [
          ...prev,
          {
            message,
            model: result.model,
            stopReason: result.stopReason,
            usage: result.usage,
            iterations: result.iterations || [],
          },
        ]);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || l("COMMON.ERROR"));
    } finally {
      setLoading(false);
      setStatusText("");
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 1300,
        display: "flex",
        alignItems: "flex-end",
        gap: 1.5,
      }}
    >
      {/* TEMP DEBUG: token-usage breakdown panel — remove alongside it. */}
      {open && debugOpen ? (
        <AgentChatDebugPanel debugLog={debugLog} height={size.height} />
      ) : null}
      {open ? (
        <Paper
          elevation={6}
          sx={{
            position: "relative",
            width: size.width,
            height: size.height,
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            onMouseDown={handleResizeStart}
            title={l("AGENT_CHAT.RESIZE")}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: 16,
              height: 16,
              cursor: "nwse-resize",
              zIndex: 1,
              "&::before": {
                content: '""',
                position: "absolute",
                top: 4,
                left: 4,
                width: 8,
                height: 8,
                borderTop: "2px solid rgba(255,255,255,0.7)",
                borderLeft: "2px solid rgba(255,255,255,0.7)",
              },
            }}
          />

          <Box
            sx={{
              backgroundColor: "primary.main",
              color: "#fff",
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#fff" }}>
                {l("AGENT_CHAT.TITLE")}
              </Typography>
              {/* TEMP DEBUG: running token/cost readout — remove when done debugging. */}
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", display: "block" }}>
                DEBUG: {usageStats.tokens.toLocaleString()} tokens · ${usageStats.cost.toFixed(4)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* TEMP DEBUG: toggles the token-usage debug panel — remove alongside it. */}
              <IconButton
                size="small"
                onClick={() => setDebugOpen((v) => !v)}
                title={l("AGENT_CHAT.DEBUG_TOGGLE")}
                sx={{ color: debugOpen ? "#fff" : "rgba(255,255,255,0.6)" }}
              >
                <BugOutlined />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setOpen(false)}
                sx={{ color: "#fff" }}
              >
                <MinusOutlined />
              </IconButton>
            </Box>
          </Box>

          <Box
            ref={listRef}
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              p: 1.5,
              backgroundColor: "#f5f6f8",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {messages.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: "#888", textAlign: "center", mt: 2 }}
              >
                {l("AGENT_CHAT.EMPTY")}
              </Typography>
            ) : (
              messages.map((m, i) => {
                const displayText = getDisplayText(m.content);
                return (
                  <React.Fragment key={i}>
                    {(m.toolCalls || []).map((tc, j) => (
                      <AgentChatToolCall key={j} toolCall={tc} />
                    ))}
                    {displayText ? (
                      <Box
                        sx={{
                          alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                          backgroundColor: m.role === "user" ? "primary.main" : "#fff",
                          color: m.role === "user" ? "#fff" : "#222",
                          borderRadius: 2,
                          px: 1.5,
                          py: 1,
                          maxWidth: "80%",
                          boxShadow: "0px 1px 2px rgba(0,0,0,0.1)",
                          wordBreak: "break-word",
                          ...markdownBubbleStyles,
                        }}
                      >
                        <ReactMarkdown source={displayText} plugins={[gfm]} />
                      </Box>
                    ) : null}
                  </React.Fragment>
                );
              })
            )}
            {loading ? (
              <Box
                sx={{
                  alignSelf: "flex-start",
                  px: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress size={14} />
                <Typography variant="caption" sx={{ color: "#666" }}>
                  {statusText || l("AGENT_CHAT.THINKING")}
                </Typography>
              </Box>
            ) : null}
            {error ? (
              <Typography variant="caption" sx={{ color: "#d9534f", px: 1 }}>
                {error}
              </Typography>
            ) : null}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 1,
              p: 1,
              borderTop: "1px solid #eee",
              backgroundColor: "#fff",
              flexShrink: 0,
            }}
          >
            <TextField
              hideLabel
              placeholder={l("AGENT_CHAT.PLACEHOLDER")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              multiline
              maxRows={3}
              sx={{
                flexGrow: 1,
                "& .MuiInputBase-input": { padding: "10px 12px" },
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={loading || !input.trim()}
            >
              <SendOutlined />
            </IconButton>
          </Box>
        </Paper>
      ) : (
        <Fab
          color="primary"
          onClick={() => setOpen(true)}
          sx={{ boxShadow: "0px 4px 10px rgba(0,0,0,0.25)" }}
        >
          <MessageOutlined style={{ fontSize: 22 }} />
        </Fab>
      )}
    </Box>
  );
}

export default AgentChat;
